/**
 * Feature E02-F06-T03 — Project event timeline section.
 *
 * Renders a project's `project_events` as a vertical timeline (newest-first, the
 * last 50), each row showing an icon for the event_type, the server-rendered RU
 * `description`, the timestamp, and — only when present — the actor (`created_by`;
 * system/automatic events render without an author line). This is a NEW section:
 * the legacy SPA loaded `project.events` (dashboard.js getProject) but never had a
 * dedicated timeline UI (the old emails section was removed 2026-04-26).
 *
 * Acceptance mapping:
 *   #1 newest→oldest, max 50  → data comes pre-sorted/capped from the route; the
 *      view additionally normalizes via `orderedTimeline` (defensive).
 *   #2 event_type + date + human-readable payload → `eventGlyph` (icon/tone) +
 *      `formatEventTime` + the server `description` (via `eventDescription`).
 *   #3 created_by only if present → `authorLabel` returns null for system events,
 *      and the actor line is omitted.
 *
 * All display rules live in `./projectTimelineModel.ts` (unit-tested without a
 * DOM); the data hook is `./projectTimelineApi.useProjectEvents`. This component
 * is the thin React shell composing them.
 *
 * Wire-up (the serial Wire pass mounts this inside the project detail view; it is
 * a self-contained section that takes a `projectId`):
 *   import { ProjectTimeline } from '../projects/ProjectTimeline.js';
 *   …
 *   <ProjectTimeline projectId={project.id} />
 */
import { useState } from 'react';
import type { ProjectEventDTO } from '@eer/shared';
import { useProjectEvents } from './projectTimelineApi.js';
import {
  authorLabel,
  eventDescription,
  eventGlyph,
  formatEventTime,
  orderedTimeline,
  type TimelineTone,
} from './projectTimelineModel.js';

/**
 * How many events to show while collapsed (PK-QA-15). The list is already
 * newest-first, so the first `COLLAPSED_COUNT` are the most recent ones.
 */
const COLLAPSED_COUNT = 3;

/** Tailwind colour classes per tone bucket (dot background + icon text). */
const TIMELINE_TONE_CLASS: Record<TimelineTone, string> = {
  create: 'bg-green-100 text-green-700',
  ai: 'bg-purple-100 text-purple-700',
  email: 'bg-blue-100 text-blue-700',
  send: 'bg-amber-100 text-amber-700',
  neutral: 'bg-gray-100 text-gray-600',
};

/** A single timeline row (dot + description + meta). */
function TimelineRow({
  event,
  last,
}: {
  event: ProjectEventDTO;
  last: boolean;
}): React.JSX.Element {
  const glyph = eventGlyph(event.event_type);
  const author = authorLabel(event.created_by);
  const time = formatEventTime(event.created_at);
  return (
    <li className="relative flex gap-3 pb-5" data-testid={`timeline-event-${event.id}`}>
      {/* Vertical connector line (hidden on the last VISIBLE row). */}
      {last ? null : (
        <span
          aria-hidden
          data-testid="timeline-connector"
          className="absolute left-[15px] top-8 h-full w-px bg-border"
        />
      )}
      {/* Dot */}
      <span
        className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${TIMELINE_TONE_CLASS[glyph.tone]}`}
        data-testid="timeline-event-dot"
        data-event-type={event.event_type}
      >
        <i className={`${glyph.icon} text-xs`} aria-hidden />
      </span>
      {/* Body */}
      <div className="min-w-0 flex-1 pt-1">
        <p data-testid="timeline-event-description" className="text-sm text-text-strong">
          {eventDescription(event)}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-400">
          {time ? <time data-testid="timeline-event-time">{time}</time> : null}
          {author ? (
            <>
              <span aria-hidden>·</span>
              <span data-testid="timeline-event-author">
                <i className="fa-solid fa-user mr-1" aria-hidden />
                {author}
              </span>
            </>
          ) : null}
        </div>
      </div>
    </li>
  );
}

export interface ProjectTimelineProps {
  /** The project whose events to show. When null/0, the section renders idle. */
  readonly projectId: number | null | undefined;
}

/**
 * The project event-timeline section. Self-contained: give it a `projectId` and
 * it fetches the last 50 events newest-first, then shows the 3 most recent by
 * default with a "Load more" link that reveals the rest all at once (PK-QA-15 —
 * pure client-side presentational collapse over the already-loaded ≤50 list).
 */
export function ProjectTimeline({ projectId }: ProjectTimelineProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const query = useProjectEvents(projectId);
  const events = orderedTimeline(query.data ?? []);
  const isEmpty = query.isSuccess && events.length === 0;

  // Collapsed by default: the list is newest-first (orderedTimeline), so the first
  // COLLAPSED_COUNT are the most recent. `visible` drives BOTH the rows rendered
  // and the `last` connector recompute below.
  const visible = expanded ? events : events.slice(0, COLLAPSED_COUNT);
  const hasMore = !expanded && events.length > COLLAPSED_COUNT;

  return (
    <section data-testid="project-timeline" className="flex flex-col">
      <h3 className="mb-3 text-sm font-semibold text-text-strong">Project history</h3>

      {query.isLoading ? (
        <div data-testid="timeline-loading" className="text-sm text-gray-400">
          <i className="fa-solid fa-spinner fa-spin mr-1" aria-hidden />
          Loading events…
        </div>
      ) : query.isError ? (
        <div data-testid="timeline-error" className="text-sm text-red-600">
          Failed to load the project history.
        </div>
      ) : isEmpty ? (
        <div data-testid="timeline-empty" className="text-sm text-gray-400">
          No project events yet.
        </div>
      ) : (
        <>
          <ol data-testid="timeline-list" className="list-none">
            {visible.map((event, i) => (
              // `last` is computed from `visible` (not `events`) so the vertical
              // connector correctly hides on the last VISIBLE row in both the
              // collapsed and expanded states.
              <TimelineRow key={event.id} event={event} last={i === visible.length - 1} />
            ))}
          </ol>
          {hasMore ? (
            <button
              type="button"
              data-testid="timeline-load-more"
              onClick={() => setExpanded(true)}
              className="self-start text-sm font-medium text-blue-600 hover:underline focus-visible:underline focus-visible:outline-none"
            >
              Load more ({events.length - COLLAPSED_COUNT})
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}

export default ProjectTimeline;
